import { EmailOtpPinInput } from '@components/LoginForm/LoginForm';
import { NEXT_PUBLIC_AUTH_API_BASE_URL } from '@env';
import { getToken } from '@modules/auth-helpers';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './EmailVerify.module.scss';

const CODE_LENGTH = 6;

interface EmailVerifyProps {
  email: string;
  onVerified: () => void;
  /**
   * Verify against an OTP that was already emailed (its Stytch method id), instead of sending a new
   * code on mount. Used when the flow just sent this address a code (e.g. sign-up) so the user isn't
   * emailed twice. "Resend code" still issues a fresh code.
   */
  initialEmailId?: string;
}

function readEmailId(body: unknown): string | undefined {
  if (typeof body === 'object' && body !== null && 'email_id' in body) {
    const { email_id: emailId } = body;
    if (typeof emailId === 'string') {
      return emailId;
    }
  }
  return undefined;
}

/**
 * Email-OTP verification step for the OAuth consent flow. Sends a Stytch OTP to the user's own
 * sign-in email via the auth-service broker and verifies the entered code — both calls are
 * Firebase-bearer-authenticated and act on the caller's own linked Stytch user, so the verified
 * state is server-attested end to end (the broker re-derives it on the next attest).
 */
export default function EmailVerify({ email, onVerified, initialEmailId }: EmailVerifyProps) {
  const [emailId, setEmailId] = useState<string | undefined>(initialEmailId);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Guards the on-mount send against React strict-mode double effects.
  const requested = useRef(false);
  // Same-tick re-entry guard: PinInput can fire onChange more than once per paste, and the async
  // `busy` state can't block a second call issued before the first re-render.
  const verifying = useRef(false);

  const sendCode = useCallback(async () => {
    setBusy(true);
    setError('');
    setCode('');
    try {
      const token = await getToken();
      if (!token) {
        setError('Could not authenticate your session. Refresh the page and try again.');
        return;
      }
      const response = await fetch(`${NEXT_PUBLIC_AUTH_API_BASE_URL}/email/link`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const id = response.ok ? readEmailId(await response.json()) : undefined;
      if (!id) {
        setError('Could not send a code. Try again.');
        return;
      }
      setEmailId(id);
    } catch {
      setError('Could not send a code. Try again.');
    } finally {
      setBusy(false);
    }
  }, [email]);

  const verify = useCallback(
    async (fullCode: string) => {
      if (!emailId || verifying.current) {
        return;
      }
      verifying.current = true;
      setBusy(true);
      setError('');
      try {
        const token = await getToken();
        if (!token) {
          setError('Could not authenticate your session. Refresh the page and try again.');
          return;
        }
        const response = await fetch(`${NEXT_PUBLIC_AUTH_API_BASE_URL}/email/link/verify`, {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
          body: JSON.stringify({ email_id: emailId, code: fullCode }),
        });
        if (response.ok) {
          onVerified();
          return;
        }
        // Keep the entered code in place (clearing it would retrigger PinInput onChange, which
        // clears the error); the user edits it, which resets the error state.
        setError(
          response.status === 400 || response.status === 401
            ? 'That code didn’t work. Check it and try again.'
            : 'Something went wrong. Try again.',
        );
      } catch {
        setError('Something went wrong. Try again.');
      } finally {
        verifying.current = false;
        setBusy(false);
      }
    },
    [emailId, onVerified],
  );

  useEffect(() => {
    if (requested.current) {
      return;
    }
    requested.current = true;
    // A code was already emailed for initialEmailId — verify against it rather than sending another.
    if (!initialEmailId) {
      void sendCode();
    }
  }, [sendCode, initialEmailId]);

  return (
    <div className={styles['email-verify']}>
      <EmailOtpPinInput
        error={error}
        value={code}
        onChange={(value) => {
          setError('');
          setCode(value);
          if (value.length === CODE_LENGTH && !busy) {
            void verify(value);
          }
        }}
      />
      <div className={styles['resend-container']}>
        <p className={styles['resend-text']}>
          Didn&#39;t receive a code?{' '}
          <button
            type="button"
            className={styles['resend-button']}
            disabled={busy}
            onClick={() => void sendCode()}
          >
            Resend code
          </button>
        </p>
      </div>
    </div>
  );
}
